import { useCallback, useMemo } from 'react';
import { GateState, ReadOnlyCustomWidgetState } from '@kemu-io/kemu-core/types';
import { findRecipe } from '@kemu-io/kemu-core/common/recipeCache';
import { DEFAULT_THING_ID } from '@kemu-io/kemu-core/common/constants';


const readWidgetState = <T>(recipePoolId: string, thingId: string, widgetId: string): ReadOnlyCustomWidgetState<T> => {
	// Read the initial state from the store
	const recipe = findRecipe(recipePoolId);
	if (!recipe) {
    return {} as ReadOnlyCustomWidgetState<T>;
  }

	const block = recipe.blocks[thingId];
	if (!block) {
		return {} as ReadOnlyCustomWidgetState<T>;
	}

	const gate = block.gates[widgetId];
	if (!gate) {
		return {} as ReadOnlyCustomWidgetState<T>;
	}

	return { ...gate.state as ReadOnlyCustomWidgetState<T> };
};


/**
 * Non-reactive hook to get a SHALLOW the state of the widget directly from the recipe.
 * This is intended to be use by highly performant components as it does not perform
 * any type of cleaning of private properties.
 * 
 * WARNING: this method returns only performs a shallow copy of the widget's state object,
 * mutating nested properties of the object WILL modify the state of the widget.
 * 
 * @param recipePoolId the id of the recipe in the pool.
 * @param thingId the id of the thing in the recipe (blockRecipeId).
 */
const useGetWidgetState = <T=GateState>(recipePoolId?: string | null): (widgetId: string) => ReadOnlyCustomWidgetState<T> => {
	const thingId = DEFAULT_THING_ID;

  const getState = useCallback((widgetId: string): ReadOnlyCustomWidgetState<T> => {
		if (!recipePoolId) {
			throw new Error('recipePoolId is required');
		}

    const latestState = readWidgetState<T>(recipePoolId, thingId, widgetId);
    return latestState;
  }, [recipePoolId, thingId]);

  return getState;
};


export default useGetWidgetState;
